import { Show, createSignal, type Component } from 'solid-js';
import { KeyRound } from 'lucide-solid';
import { ApiError } from '~/shared/api/http-client';
import { authApi } from '~/shared/api/auth-api';
import { Button } from '~/shared/ui/Button';
import { DialogShell } from '~/shared/ui/DialogShell';
import { TextField } from '~/shared/ui/TextField';

export const refusalMessage = (err: unknown): string =>
  err instanceof ApiError ? err.message : 'Не удалось сменить пароль';

export const ChangePasswordDialog: Component<{
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}> = (props) => {
  const [current, setCurrent] = createSignal('');
  const [next, setNext] = createSignal('');
  const [repeat, setRepeat] = createSignal('');
  const [error, setError] = createSignal<string | null>(null);
  const [saving, setSaving] = createSignal(false);

  const mismatch = () => repeat().length > 0 && next() !== repeat();
  const canSubmit = () =>
    current().length > 0 && next().length > 0 && !mismatch() && !saving();

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!canSubmit()) return;
    setError(null);
    setSaving(true);
    try {
      await authApi.changePassword({ currentPassword: current(), newPassword: next() });
      props.onChanged();
    } catch (err) {
      setError(refusalMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Show when={props.open}>
      <DialogShell onClose={props.onClose} maxWidth="420px">
        <form class="flex flex-col gap-4 p-5" onSubmit={(e) => void submit(e)}>
          <div class="flex items-center gap-2.5">
            <KeyRound class="h-4.5 w-4.5 text-[var(--color-text-tertiary)]" aria-hidden="true" />
            <h2 class="text-[15px] font-semibold">Смена пароля</h2>
          </div>

          <p class="text-[12.5px] text-[var(--color-text-tertiary)] leading-snug">
            После смены сеансы завершатся на всех устройствах, включая этот, — потребуется войти
            заново.
          </p>

          <TextField
            label="Текущий пароль"
            type="password"
            autocomplete="current-password"
            value={current()}
            onInput={(e) => setCurrent(e.currentTarget.value)}
          />
          <TextField
            label="Новый пароль"
            type="password"
            autocomplete="new-password"
            value={next()}
            onInput={(e) => setNext(e.currentTarget.value)}
          />
          <TextField
            label="Новый пароль ещё раз"
            type="password"
            autocomplete="new-password"
            value={repeat()}
            onInput={(e) => setRepeat(e.currentTarget.value)}
            error={mismatch() ? 'Пароли не совпадают' : undefined}
          />

          <Show when={error()}>
            <p class="text-[12.5px]" style={{ color: 'var(--color-danger)' }} role="alert">
              {error()}
            </p>
          </Show>

          <div class="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="md" onClick={props.onClose}>
              Отмена
            </Button>
            <Button type="submit" variant="primary" size="md" disabled={!canSubmit()} loading={saving()}>
              Сменить пароль
            </Button>
          </div>
        </form>
      </DialogShell>
    </Show>
  );
};
